<template>
  <InvestorOnboardingStep>
    <form @submit.prevent="next">
      <FormGroup>
        <FormField label="Company Name" :error="form.fieldErrors('companyName')">
          <Input v-model="form.fields.companyName" placeholder="Acme Property Group" />
        </FormField>

        <FormField label="Team size" :error="form.fieldErrors('numberOfEmployees')">
          <Select v-model="form.fields.numberOfEmployees">
            <option disabled value="">Select team size</option>
            <option v-for="opt in companyNumberOfEmployeesOptions" :key="opt" :value="opt">
              {{ opt }}
            </option>
          </Select>
        </FormField>

        <FormField label="How many units do you oversee?" :error="form.fieldErrors('numberOfUnitsOverseeing')">
          <Input type="number" v-model="form.fields.numberOfUnitsOverseeing" placeholder="e.g. 42" />
        </FormField>

        <FormField label="What would you like Tallo to help you with?">
          <Textarea maxlength="200" v-model="form.fields.perceivedProductValueProposition" />
        </FormField>

        <View justify="end" gap="sm">
          <Button variant="ghost" type="button" :disabled="form.submitting" @click="goBack">Back</Button>
          <Button type="submit" :disabled="!canContinue" :loading="form.submitting">Continue</Button>
        </View>
      </FormGroup>
    </form>
  </InvestorOnboardingStep>
</template>

<script setup lang="ts">
import { minValue, helpers, required } from '@vuelidate/validators';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import {
  Button,
  FormField,
  FormGroup,
  Input,
  Select,
  Textarea,
  View,
  extractErrorMessage,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import { CompanyNumberOfEmployees, companyNumberOfEmployeesOptions } from '@tallo/investor/company';
import {
  InvestorOnboardingStep as OnboardingStep,
  useOnboardingStep,
  useOnboardingStore,
} from '@tallo/investor/investor';
import { AppRouteNames } from '@tallo/investor/navigation';

import InvestorOnboardingStep from '../components/investor-onboarding-step.vue';

const store = useOnboardingStore();
const router = useRouter();
const alerts = useAlertsStore();

const companyNameValidator = helpers.withMessage(
  'Please enter a valid company name (not only numbers)',
  (value: string) => {
    if (!value) return false;
    return !/^\d+$/.test(value);
  },
);

const form = useForm<{
  companyName: string;
  numberOfEmployees: CompanyNumberOfEmployees | '';
  numberOfUnitsOverseeing: number | null;
  perceivedProductValueProposition: string;
}>(
  {
    companyName: store.companyName,
    numberOfEmployees: (store.numberOfEmployees as CompanyNumberOfEmployees) || '',
    numberOfUnitsOverseeing: store.numberOfUnitsOverseeing,
    perceivedProductValueProposition: store.perceivedProductValueProposition,
  },
  {
    companyName: { required, companyName: companyNameValidator },
    numberOfUnitsOverseeing: { minValue: minValue(1) },
  },
);

const isFormValid = computed(() => {
  return (
    !!form.fields.companyName && !!form.fields.numberOfEmployees && (form.fields.numberOfUnitsOverseeing ?? 0) >= 1
  );
});

// useForm owns validation, the re-entrancy guard and server field errors, so it drives this step's save
const { goBack, canContinue, onContinue } = useOnboardingStep(() => isFormValid.value && !form.submitting);

async function next() {
  if (!canContinue.value) return;

  try {
    await form.submit(async (fields) => {
      await store.saveStep(OnboardingStep.CompanyInfo, {
        companyName: fields.companyName,
        numberOfEmployees: fields.numberOfEmployees || undefined,
        numberOfUnitsOverseeing:
          fields.numberOfUnitsOverseeing == null ? undefined : Number(fields.numberOfUnitsOverseeing),
        perceivedProductValueProposition: fields.perceivedProductValueProposition || undefined,
      });
    });
    router.push({ name: AppRouteNames.INVESTOR_ONBOARDING_INVITE_TEAM });
  } catch (error) {
    // Vuelidate rejects with a string when the form is invalid; the field errors already show it
    if (typeof error === 'string') return;
    alerts.error(extractErrorMessage(error, "We couldn't save your company details. Please try again."));
  }
}

onContinue(next);
</script>
